import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import styles from "../../../styles/pages/profile/ProfilePage.module.css";
import {
  FaComment,
  FaTrash,
  FaExclamationTriangle,
  FaClock,
  FaThumbsUp,
  FaSort,
  FaEdit,
} from "react-icons/fa";
import { formatDate, formatRelativeTime } from "../../../utils/dateFormatter";
import api from "../../../utils/api/axiosInstance";
import CommentsViewSkeleton from "./CommentsViewSkeleton";
import ConfirmModal from "../../common/ConfirmModal";
import useBulkSelection from "../bulk/useBulkSelection";
import BulkSelectBar, {
  SelectCheckbox,
  SelectModeButton,
} from "../bulk/BulkSelectBar";
import bulkStyles from "../bulk/BulkSelectBar.module.css";
import runBulkRequests from "../../../utils/runBulkRequests";

const CommentsView = ({
  comments,
  loading,
  error,
  onRefresh,
  showToast,
  currentTab,
}) => {
  const [sortBy, setSortBy] = useState("date"); // 'date' | 'votes'
  const [deleting, setDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [pendingIds, setPendingIds] = useState([]);

  // Sort comments
  const sortedComments = useMemo(() => {
    let result = [...comments];

    result.sort((a, b) => {
      if (sortBy === "date") {
        return new Date(b.createdAt) - new Date(a.createdAt);
      } else if (sortBy === "votes") {
        return (b.upvotes || 0) - (a.upvotes || 0);
      }
      return 0;
    });

    return result;
  }, [comments, sortBy]);

  const visibleIds = useMemo(
    () => sortedComments.map((comment) => comment._id).filter(Boolean),
    [sortedComments],
  );
  const {
    selectMode,
    selectedIds,
    allVisibleSelected,
    toggle,
    selectAllVisible,
    exitSelectMode,
    toggleSelectMode,
    setSelectedIds,
  } = useBulkSelection(visibleIds);

  const openDelete = (ids) => {
    if (!ids.length || deleting) return;
    setPendingIds(ids);
    setShowDeleteModal(true);
  };

  const confirmDeleteComment = async () => {
    if (pendingIds.length === 0) return;
    const ids = pendingIds;

    try {
      setDeleting(true);
      setShowDeleteModal(false);
      const { succeeded, failed, failedIds } = await runBulkRequests(ids, (id) =>
        api.delete(`/posts/comments/${id}`),
      );
      if (succeeded > 0) {
        showToast({
          message:
            succeeded === 1
              ? "Comment deleted successfully"
              : `${succeeded} comments deleted`,
          type: "success",
        });
        onRefresh();
      }
      if (failed > 0) {
        showToast({
          message:
            succeeded === 0
              ? "Failed to delete comment"
              : `${failed} comments could not be deleted`,
          type: "error",
        });
        setSelectedIds(failedIds);
      } else if (selectMode) {
        exitSelectMode();
      }
    } catch (err) {
      console.error("Error deleting comment:", err);
      showToast({
        message: err.response?.data?.message || "Failed to delete comment",
        type: "error",
      });
    } finally {
      setDeleting(false);
      setPendingIds([]);
    }
  };

  const getContentLink = (comment) => {
    // Determine the link based on comment type
    let path;
    if (comment.post) {
      const postId = comment.post._id || comment.post;
      path = `/post/${postId}`;
    } else {
      return "7";
    }
    return path;
  };

  const getContentTitle = (comment) => {
    if (comment.post?.title) return comment.post.title;
    if (comment.question?.title) return comment.question.title;
    return "Content";
  };

  const getContentType = (comment) => {
    if (comment.post) return "Post";
    if (comment.question) return "Question";
    return "Comment";
  };

  if (loading) {
    return <CommentsViewSkeleton />;
  }

  if (error) {
    return (
      <div className={styles.contentSection}>
        <div className={styles.errorState}>
          <div className={styles.errorStateIcon}>
            <FaExclamationTriangle size={48} />
          </div>
          <h3>Unable to Load Comments</h3>
          <p>{error}</p>
          <button className="buttonPrimary" onClick={onRefresh}>
            <FaClock /> Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.contentSection}>
      <div className={styles.sectionHeader}>
        <div>
          <h2 className={styles.sectionTitle}>
            <FaComment className={styles.sectionIcon} />
            Comments
          </h2>
          {comments.length > 0 && (
            <p className={styles.sectionSubtitle}>
              {sortedComments.length} comment
              {sortedComments.length === 1 ? "" : "s"}
            </p>
          )}
        </div>
        {comments.length > 0 && (
          <div className={styles.bulkActions}>
            <button
              className="buttonSecondary"
              onClick={onRefresh}
              disabled={loading}
              title="Refresh Comments"
            >
              <FaClock /> Refresh
            </button>
            <SelectModeButton
              active={selectMode}
              onClick={toggleSelectMode}
              disabled={deleting}
            />
          </div>
        )}
      </div>

      {selectMode && (
        <BulkSelectBar
          selectedCount={selectedIds.length}
          allSelected={allVisibleSelected}
          busy={deleting}
          onSelectAll={selectAllVisible}
          onDelete={() => openDelete(selectedIds)}
          noun="comments"
        />
      )}

      {comments.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyStateIcon}>
            <FaComment size={64} />
          </div>
          <h3>No Comments Yet</h3>
          <p>
            You haven't commented on any posts or questions yet. Your comments
            will appear here when you join the conversation.
          </p>
          <Link to="/posts" className="buttonPrimary">
            Browse Posts
          </Link>
        </div>
      ) : (
        <>
          {/* Sort */}
          <div className={styles.filterBar}>
            <div className={styles.filterGroup}>
              <FaSort className={styles.filterIcon} />
              <label>Sort by:</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className={styles.filterSelect}
              >
                <option value="date">Most Recent</option>
                <option value="votes">Most Votes</option>
              </select>
            </div>
          </div>

          {/* Comments Timeline */}
          <div className={styles.answersGrid}>
            {sortedComments.map((comment) => {
              const isSelected = selectedIds.includes(comment._id);
              return (
              <div
                key={comment._id}
                className={`${styles.answerCard} ${
                  selectMode ? bulkStyles.selectable : ""
                } ${isSelected ? bulkStyles.cardSelected : ""}`}
                onClick={(event) => {
                  if (!selectMode) return;
                  if (event.target.closest("a, button, input, label")) return;
                  toggle(comment._id);
                }}
              >
                <div className={styles.answerCardHeader}>
                  <div className={styles.answerQuestionInfo}>
                    <div className={styles.answerQuestionDetails}>
                      <Link
                        to={getContentLink(comment)}
                        state={{ fromProfileTab: currentTab }}
                        className={styles.answerQuestionTitle}
                      >
                        {getContentTitle(comment)}
                      </Link>
                      <div className={styles.answerHeaderInfo}>
                        <span className={styles.answerStat}>
                          <FaThumbsUp /> {comment.likeCount || 0} votes
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className={styles.answerCardMeta}>
                    <span className={styles.answerDate}>
                      {formatDate(comment.createdAt)}
                    </span>
                  </div>

                  <div className={styles.answerActions}>
                    {selectMode ? (
                      <SelectCheckbox
                        checked={isSelected}
                        onChange={() => toggle(comment._id)}
                        label={`Select comment on ${getContentTitle(comment)}`}
                        disabled={deleting}
                      />
                    ) : (
                      <>
                        {getContentLink(comment) !== "7" && (
                          <Link
                            to={getContentLink(comment)}
                            state={{ fromProfileTab: currentTab }}
                            className={styles.answerActionButton}
                            title="View Context"
                          >
                            View
                          </Link>
                        )}
                        {getContentLink(comment) !== "7" && (
                          <Link
                            to={getContentLink(comment)}
                            state={{
                              fromProfileTab: currentTab,
                              editCommentId: comment._id,
                            }}
                            className={styles.answerActionButton}
                            title="Edit Comment"
                          >
                            <FaEdit size={18} />
                          </Link>
                        )}
                        <button
                          onClick={() => openDelete([comment._id])}
                          className={`${styles.answerActionButton} ${styles.answerDeleteButton}`}
                          disabled={deleting}
                          title="Delete Comment"
                        >
                          <FaTrash size={16} />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {(comment.text || "").trim() && (
                  <div className={styles.activityCommentText}>
                    {comment.text.trim()}
                  </div>
                )}
              </div>
              );
            })}
          </div>
        </>
      )}

      {showDeleteModal && (
        <ConfirmModal
          message={
            pendingIds.length > 1
              ? `Delete ${pendingIds.length} comments?`
              : "Are you sure you want to delete this comment?"
          }
          onConfirm={confirmDeleteComment}
          onClose={() => {
            if (deleting) return;
            setShowDeleteModal(false);
            setPendingIds([]);
          }}
        />
      )}
    </div>
  );
};

export default React.memo(CommentsView);
